import React, { useState, useEffect } from 'react';
import { X, ArrowUp, ArrowDown, Star, GripVertical } from 'lucide-react';
import { Button, IconButton, Spinner } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { statuses as statusService, stages as stageService } from '../../services/apiService';
import { formatApiError } from '../../utils/formatApiError';
import { DEFAULT_STATUS_COLOR } from '../../utils/colorUtils';
import ModalPortal from '../UI/ModalPortal';
import ModalFrame from '../UI/ModalFrame';

/**
 * StageStatusesModal
 *
 * Управляет связкой talent_stage_statuses для одной стадии: две колонки —
 * доступные статусы слева, назначенные (в порядке display_order) справа.
 * Порядок в правой колонке имеет смысл: первый элемент — дефолтный статус
 * стадии, тот, что подставляется при переезде на стадию с недопустимым
 * статусом. Поэтому reorder, а не просто чекбоксы.
 */
const StageStatusesModal = ({ isOpen, onClose, stage, onSaved }) => {
  const [allStatuses, setAllStatuses] = useState([]);
  const [assigned, setAssigned] = useState([]); // ordered array of status objects
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activePane, setActivePane] = useState('assigned');

  useEffect(() => {
    if (isOpen && stage) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, stage?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [allRes, linksRes] = await Promise.all([
        // Только статусы талантов: у статусов заявок, постов и шаблонов стадий нет
        statusService.getAllStatuses({ all: true, active_only: 'true', talent_type: 'talent' }),
        stageService.getStageStatusLinks(stage.id)
      ]);

      const all = Array.isArray(allRes?.data) ? allRes.data : [];
      const links = Array.isArray(linksRes?.data) ? linksRes.data : [];

      setAllStatuses(all);
      setAssigned(
        links
          .slice()
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(link => link.status)
          .filter(Boolean)
      );
    } catch (err) {
      console.error('Error loading stage statuses:', err);
      toast.error(formatApiError(err, 'Error loading statuses'));
    } finally {
      setLoading(false);
    }
  };

  const assignedIds = new Set(assigned.map(s => s.id));
  const available = allStatuses
    .filter(s => !assignedIds.has(s.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const addStatus = (status) => setAssigned(prev => [...prev, status]);
  const removeStatus = (statusId) => setAssigned(prev => prev.filter(s => s.id !== statusId));

  const moveUp = (index) => {
    if (index === 0) return;
    setAssigned(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index) => {
    setAssigned(prev => {
      if (index === prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  // Drag-and-drop переставляет элемент целиком на новую позицию (не своп
  // соседей, как у стрелок) — так же, как в StageList.jsx.
  const moveTo = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || toIndex < 0) return;
    setAssigned(prev => {
      if (toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await stageService.setStageStatuses(stage.id, assigned.map(s => s.id));
      toast.success(`Statuses for "${stage.name}" saved`);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving stage statuses:', err);
      toast.error(formatApiError(err, 'Error saving statuses'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !stage) return null;

  return (
    <ModalPortal onClose={saving ? undefined : onClose} ariaLabel="Stage statuses">
      <ModalFrame size="md" className="-m-3 w-[calc(100%+1.5rem)] rounded-none sm:m-0 sm:w-full sm:rounded-lg">
        <ModalFrame.Header className="items-start">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
            Statuses for "{stage.name}"
          </h3>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-1">
            The top status on the right is the stage default
          </p>
        </div>
        <button type="button" onClick={onClose} disabled={saving} aria-label="Close" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-light-text-tertiary hover:bg-light-bg-tertiary dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50">
          <X className="h-5 w-5" />
        </button>
        </ModalFrame.Header>

        <ModalFrame.Body>
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Spinner size="large" color="primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 rounded-lg bg-light-bg-tertiary p-1 dark:bg-dark-bg-tertiary md:hidden">
              {['available', 'assigned'].map((pane) => (
                <button
                  key={pane}
                  type="button"
                  onClick={() => setActivePane(pane)}
                  className={`rounded-md px-3 py-2 text-sm font-medium capitalize ${activePane === pane ? 'bg-light-bg-primary text-light-text-primary shadow-sm dark:bg-dark-bg-secondary dark:text-dark-text-primary' : 'text-light-text-secondary dark:text-dark-text-secondary'}`}
                >
                  {pane}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className={`${activePane === 'available' ? 'flex' : 'hidden'} min-h-0 flex-col md:flex`}>
              <h4 className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2 flex-shrink-0">Available</h4>
              <div className="space-y-1">
                {available.length === 0 && (
                  <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary italic">Nothing left to add</p>
                )}
                {available.map(status => (
                  <button
                    key={status.id}
                    type="button"
                    onClick={() => addStatus(status)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded-md hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary"
                  >
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: status.color || DEFAULT_STATUS_COLOR }} />
                    <span className="truncate">{status.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={`${activePane === 'assigned' ? 'flex' : 'hidden'} min-h-0 flex-col md:flex`}>
              <h4 className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-2 flex-shrink-0">Assigned to this stage</h4>
              <div className="space-y-1">
                {assigned.length === 0 && (
                  <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary italic">No statuses assigned yet</p>
                )}
                {assigned.map((status, index) => (
                  <div
                    key={status.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', String(index))}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
                      if (!isNaN(fromIndex)) moveTo(fromIndex, index);
                    }}
                    className="flex items-center gap-1 px-2 py-1.5 text-sm rounded-md bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary cursor-move"
                  >
                    <GripVertical className="w-3.5 h-3.5 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0" />
                    {index === 0 && <Star className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" titleAccess="Default status" />}
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: status.color || DEFAULT_STATUS_COLOR }} />
                    <span className="truncate flex-1">{status.name}</span>
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="neutral"
                      size="small"
                      aria-label="Move up"
                      title="Move up"
                      disabled={index === 0}
                      onClick={() => moveUp(index)}
                      icon={<ArrowUp className="w-3.5 h-3.5" />}
                    />
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="neutral"
                      size="small"
                      aria-label="Move down"
                      title="Move down"
                      disabled={index === assigned.length - 1}
                      onClick={() => moveDown(index)}
                      icon={<ArrowDown className="w-3.5 h-3.5" />}
                    />
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="error"
                      size="small"
                      aria-label="Remove from stage"
                      title="Remove from stage"
                      onClick={() => removeStatus(status.id)}
                      icon={<X className="w-3.5 h-3.5" />}
                    />
                  </div>
                ))}
              </div>
            </div>
            </div>
          </div>
        )}
        </ModalFrame.Body>

        <ModalFrame.Footer className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
          <Button type="button" variant="outlined" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" color="primary" onClick={handleSave} disabled={loading || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </ModalFrame.Footer>
      </ModalFrame>
    </ModalPortal>
  );
};

export default StageStatusesModal;
